import React, { useState, useEffect, ReactElement } from 'react';
import { useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { actions as newProjectWizardActions } from '../../../actions/newProjectWizardActions';
import * as types from '../../../constants/actionTypes';
import { NewProjectData, BaselineOption } from '../../../types/common';
import Notification from '../../Common/Notification';
import TutorialModal from './TutorialModal';
import './LinkCopyTasks.scss';

interface CopyTasksProps {
	setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
	setProjectData: React.Dispatch<React.SetStateAction<NewProjectData>>;
	chooseDiffAddTaskMethod?: () => void; // Made nullable
}

interface ProjectAndBaselines {
	projectId: number;
	projectName: string;
	baselineId: number;
	baselineName: string;
}

const CopyTasks = ({ setIsLoading, setProjectData, chooseDiffAddTaskMethod }: CopyTasksProps): ReactElement => {
	const dispatch: Dispatch = useDispatch();
	const listCopyBaselines = async () => dispatch(newProjectWizardActions.listCopyBaselines());

	const [availableProjects, setAvailableProjects] = useState<Array<ProjectAndBaselines>>([]); // Keeping available & selected projects here, not in parent
	const [selectedProjects, setSelectedProjects] = useState<Array<ProjectAndBaselines>>([]);
	const [showTutorial, setShowTutorial] = useState<boolean>(false);
	const [availableFilter, setAvailableFilter] = useState<string>('');
	const [selectedFilter, setSelectedFilter] = useState<string>('');

	useEffect(() => {
		loadFilters();
	}, []);

	const loadFilters = async () => {
		setIsLoading(true);

		try {
			const response = await listCopyBaselines();
			if (response.type === types.NEW_PROJECT_WIZARD_LIST_COPY_BASELINES_SUCCESS) {
				setAvailableProjects(response.payload.projects);
			} else if (response.type === types.NEW_PROJECT_WIZARD_LIST_COPY_BASELINES_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			setIsLoading(false);
		}
	};

	const renderAvailableProjects = () => {
		const filterLower = availableFilter.toLocaleLowerCase();

		return availableProjects
			.filter((x) => !selectedProjects.some((sl) => sl.baselineId === x.baselineId)) // Suppress any projects that have been selected
			.filter((x) => x.projectName.toLocaleLowerCase().indexOf(filterLower) >= 0)
			.sort((a, b) => (a.projectName > b.projectName ? 1 : -1))
			.map((baseline, index) => (
				<div
					className='list-group-item'
					key={'avail-baseline-' + index}
					id={baseline.baselineId.toString()}
					onClick={() => selectBaseline(baseline.baselineId)}>
					<div className='d-flex w-100 justify-content-between'>
						<div className='flex-grow-1'>
							<p className='list-item-baseline'>{baseline.projectName}</p>
							<p className='list-item-projects'>{baseline.baselineName}</p>
						</div>

						<div className='list-item-icon'>
							<i className='fa-regular fa-plus'></i>
						</div>
					</div>
				</div>
			));
	};

	const renderSelectedProjects = () => {
		const filterLower = selectedFilter.toLocaleLowerCase();

		return selectedProjects
			.filter((x) => x.projectName.toLocaleLowerCase().indexOf(filterLower) >= 0)
			.sort((a, b) => (a.projectName > b.projectName ? 1 : -1))
			.map((baseline, index) => (
				<div
					className='list-group-item'
					key={'selected-baseline-' + index}
					id={baseline.baselineId.toString()}
					onClick={() => deselectBaseline(baseline.baselineId)}>
					<div className='d-flex w-100 justify-content-between'>
						<div className='flex-grow-1'>
							<p className='list-item-baseline'>{baseline.projectName}</p>
							<p className='list-item-projects'>{baseline.baselineName}</p>
						</div>
						<div className='list-item-icon'>
							<i className='fa-regular fa-minus'></i>
						</div>
					</div>
				</div>
			));
	};

	const selectBaseline = (baselineId: number) => {
		const selected = availableProjects.find((x) => x.baselineId === baselineId);

		setAvailableProjects((existing) => existing.filter((x) => x.baselineId !== baselineId));
		setSelectedProjects((existing) => [...existing, selected]);

		setProjectData((existing) => ({
			...existing,
			baselineIds: [...existing.baselineIds, selected.baselineId],
		}));
	};

	const deselectBaseline = (baselineId: number) => {
		const selected = selectedProjects.find((x) => x.baselineId === baselineId);

		setSelectedProjects((existing) => existing.filter((x) => x.baselineId !== baselineId));
		setProjectData((existing) => ({
			...existing,
			baselineIds: existing.baselineIds.filter((x) => x !== baselineId),
		}));

		setAvailableProjects((existing) => [...existing, selected]);
	};

	const selectAll = () => {
		setSelectedProjects((existing) => [...existing, ...availableProjects]);
		setAvailableProjects([]);
		
		// Update projectData with all the baseline IDs from available projects
		setProjectData((existing) => ({
			...existing,
			baselineIds: [...existing.baselineIds, ...availableProjects.map(project => project.baselineId)],
		}));
	};

	const deselectAll = () => {
		setAvailableProjects((existing) => [...existing, ...selectedProjects]);
		
		// Clear all baseline IDs from projectData
		setProjectData((existing) => ({
			...existing,
			baselineIds: existing.baselineIds.filter(id => !selectedProjects.some(project => project.baselineId === id)),
		}));
		
		setSelectedProjects([]);
	};

	return (
		<div className='copy-tasks-content'>
			<div className='wizard-content-title d-flex justify-content-between align-items-center'>
				<span>Add tasks to your project by copying tasks from a previous project.</span>
				<button type='button' className='btn btn-link' onClick={() => setShowTutorial(true)}>
					<i className='fa-regular fa-info-circle fa-lg'></i>&nbsp;&nbsp;Learn how Pacer organizes tasks.
				</button>
			</div>

			{chooseDiffAddTaskMethod && (
				<div className='select-diff-method'>
					<button type='button' className='btn btn-link' onClick={() => chooseDiffAddTaskMethod()}>
						<i className='fa-regular fa-chevron-left fa-lg'></i>
						&nbsp;&nbsp;Select Different Add Task Method
					</button>
				</div>
			)}

			<div className='container'>
				<div className='row'>
					{/* Begin available */}
					<div className='col-md-6'>
						<div className='row baselines-section-top'>
							<div className='col-9 my-3'>
								<span className='wizard-content-title'>Available</span>
							</div>
							<div className='col-3'>{/* Future spot of "create new task list" */}</div>
						</div>

						<div className='row baselines-section'>
							<div className='col-12 py-2'>
								<div className='input-group'>
									<input
										type='text'
										className='form-control'
										id='search-available'
										placeholder='Search by name'
										aria-label='Search by name'
										aria-describedby='search-addon'
										value={availableFilter}
										onChange={(e) => setAvailableFilter(e.target.value)}
									/>
									<span className='input-group-text bg-transparent' id='search-addon'>
										<i className='fa-regular fa-magnifying-glass'></i>
									</span>
								</div>
							</div>
						</div>

						<div className='row baselines-section'>
							<div className='col-12 list-container overflow-auto'>
								<div className='list-group'>{renderAvailableProjects()}</div>
							</div>
						</div>

						<div className='row baselines-section-bottom'>
							<div className='col-12'>
								<button type='button' className='btn pacer61-purple-btn' onClick={() => selectAll()}>
									Select All
								</button>
							</div>
						</div>
					</div>

					{/* Begin selected */}
					<div className='col-md-6'>
						<div className='row baselines-section-top'>
							<div className='col-9 my-3'>
								<span className='wizard-content-title'>Selected</span>
							</div>
						</div>

						<div className='row baselines-section'>
							<div className='col-12 py-2'>
								<div className='input-group'>
									<input
										type='text'
										className='form-control'
										id='search-selected'
										placeholder='Search by name'
										aria-label='Search by name'
										aria-describedby='search-addon'
										value={selectedFilter}
										onChange={(e) => setSelectedFilter(e.target.value)}
									/>
									<span className='input-group-text bg-transparent' id='search-addon'>
										<i className='fa-regular fa-magnifying-glass'></i>
									</span>
								</div>
							</div>
						</div>

						<div className='row baselines-section'>
							<div className='col-12 list-container overflow-auto'>
								<ul className='list-group'>{renderSelectedProjects()}</ul>
							</div>
						</div>

						<div className='row baselines-section-bottom'>
							<div className='col-12'>
								<button type='button' className='btn pacer61-purple-btn' onClick={() => deselectAll()}>
									Remove All
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>

			{showTutorial && <TutorialModal setShowTutorial={setShowTutorial} />}
		</div>
	);
};

export default CopyTasks;
